import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, Download, Search } from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { OuraListParams } from "@/types/oura";

type Props<T> = {
  columns: ColumnDef<T>[];
  data: T[];
  total: number;
  params: OuraListParams;
  onParamsChange: (params: OuraListParams) => void;
  exportName: string;
  searchPlaceholder?: string;
  actionButton?: ReactNode;
  onRowClick?: (row: T) => void;
};

function csvValue(value: unknown) {
  if (value === null || value === undefined) return "";
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

export function AdminDataTable<T extends object>({
  columns,
  data,
  total,
  params,
  onParamsChange,
  exportName,
  searchPlaceholder = "Search",
  actionButton,
  onRowClick,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>(
    params.sort ? [{ id: params.sort, desc: params.order !== "asc" }] : [],
  );

  const page = params.page ?? 1;
  const pageSize = params.page_size ?? 10;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      setSorting(next);
      const first = next[0];
      onParamsChange({
        ...params,
        page: 1,
        sort: first?.id,
        order: first?.desc ? "desc" : "asc",
      });
    },
  });

  const visibleColumns = useMemo(
    () => table.getAllLeafColumns().filter((column) => column.id !== "select"),
    [table],
  );

  const exportCsv = () => {
    const headers = visibleColumns.map((column) => column.id);
    const rows = data.map((row) =>
      headers.map((key) => csvValue((row as Record<string, unknown>)[key])).join(","),
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = exportName;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
          <Input
            className="pl-9"
            placeholder={searchPlaceholder}
            value={params.search ?? ""}
            onChange={(event) => onParamsChange({ ...params, page: 1, search: event.target.value })}
          />
        </div>
        <div className="flex items-center gap-2">
          {actionButton}
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onParamsChange({ ...params, page: 1, page_size: Number(value) })}
          >
            <SelectTrigger className="w-[108px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={exportCsv} aria-label="Export CSV">
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="rounded border border-zinc-800">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={header.column.getCanSort() ? "cursor-pointer select-none" : undefined}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() === "asc" ? " ^" : ""}
                    {header.column.getIsSorted() === "desc" ? " v" : ""}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={onRowClick ? "cursor-pointer" : undefined}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-zinc-500">
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm text-zinc-400">
        <span>
          Page {page} of {pageCount} - {total} total
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            disabled={page <= 1}
            onClick={() => onParamsChange({ ...params, page: page - 1 })}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={page >= pageCount}
            onClick={() => onParamsChange({ ...params, page: page + 1 })}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
